import { QueryClient, QueryFunction } from '@tanstack/react-query';
import { isNil, isObject } from 'lodash-es';
import { OneServiceNoParams, OneServiceWithParams } from 'types/services/services';

/**
 * Create a tanstack query fetcher from a service without params fetcher.
 * @param service the service without params to use
 * @param queryClient the tanstack query client
 * @return the tanstack query function
 */
function serviceFetcherToTQFunction<Data>(service: OneServiceNoParams<Data>, queryClient: QueryClient): QueryFunction<Data, readonly string[]>;

/**
 * Create a tanstack query fetcher from a service with params fetcher.
 * @param service the service with params to use
 * @param queryClient the tanstack query client
 * @return the tanstack query function
 */
function serviceFetcherToTQFunction<Data, Params extends object>(
  service: OneServiceWithParams<Data, Params>,
  queryClient: QueryClient,
): QueryFunction<Data>;

/**
 * Create a tanstack query fetcher from a service fetcher.
 * @param service the service to use
 * @param queryClient the tanstack query client
 * @return the tanstack query function
 */
function serviceFetcherToTQFunction<Data, Params extends object>(
  service: OneServiceNoParams<Data> | OneServiceWithParams<Data, Params>,
  queryClient: QueryClient,
): QueryFunction<Data, readonly [string] | readonly [string, Params]> {
  /**
   * Tanstack query function
   * @param params of the TQ query functions
   * @returns the data or promise of the data from the service
   */
  const tqFetcher: QueryFunction<Data, readonly [string] | readonly [string, Params]> = ({ queryKey }) => {
    // get the service params from TQ query key
    const [, params] = queryKey;

    // build the service context
    const context = { queryClient };

    if (!isNil(params) && isObject(params)) {
      // service with params
      const { fetcher } = service as OneServiceWithParams<Data, Params>;

      return fetcher(params, context);
    }

    // service without params
    const { fetcher } = service as OneServiceNoParams<Data>;

    return fetcher(context);
  };

  return tqFetcher;
}

export default serviceFetcherToTQFunction;
